/**
 * Typed client for the §20.1 public API.
 *
 * §20.1 ends with a sentence that shapes this whole file: *"Все mutation
 * endpoints требуют idempotency key и actor identity."* Every mutation here
 * therefore takes a `MutationContext` as a required argument rather than reading
 * one from module state — a required parameter cannot be forgotten, and the
 * at-least-once delivery model in ADR-003 means a retried POST without a key is
 * a duplicate effect, not a duplicate request.
 *
 * Errors are turned into `ApiError` with the *source* attached, because §16.3's
 * Error row requires a "source-specific error and retry" rather than a generic
 * failure toast.
 */

import type {
  ConsoleDataSource,
  MutationContext,
  SceneStreamHandlers,
  Subscription,
} from './data-source.js';
import { subscribeToScene } from './sse.js';
import { releaseDetailApiPath, releaseListApiPath } from '../logic/releases.js';
import type {
  HomeThreadMessage,
  TeamListItem,
  ApprovalReceiptView,
  ArtifactView,
  AuditEntry,
  BoardView,
  SkillContentView,
  TeamVersionSummary,
  EvidenceView,
  GateView,
  IntegrationView,
  MainOperatorThread,
  OperatorChatSummary,
  OperatorChatThread,
  OperatorStatusView,
  HumanReviewRequestView,
  ReleaseOperationView,
  ReleasePageView,
  RoleSummary,
  RunView,
  TaskChecklistView,
  SceneSnapshot,
  SkillPackageView,
  TaskListItem,
  TaskSnapshotView,
  TeamStatus,
  TeamTemplate,
  ThreadView,
  ValidationFinding,
  ValidationReport,
  ExecutionEnvironmentSummary,
  CredentialSummaryView,
  ManagedLoginView,
  ProviderLoginSessionView,
  ProviderConnectionView,
  StageEntryNotificationView,
  HarnessSummary,
  WorkflowView,
  WorkflowLayoutView,
  PackageOption,
  WebhookBatchView,
  WebhookEndpointView,
  WebhookInputView,
  WebhookSecretView,
} from './types.js';

export class ApiError extends Error {
  readonly source: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly code: string | null;

  constructor(source: string, status: number, message: string, retryable: boolean, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.source = source;
    this.status = status;
    this.retryable = retryable;
    this.code = code;
  }
}

export function isTaskNotFoundError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 404 && error.code === 'task_not_found';
}

export interface ApiOptions {
  /** Base URL of the Control API. Empty string means same origin. */
  readonly baseUrl: string;
  /** Identity recorded on every mutation (§20.1). */
  readonly actorId: string;
  /** Team whose status the Console shows; the API is multi-team. */
  readonly teamId: string;
  /** Live team selection — read per request so the switcher needs no reload. */
  readonly currentTeamId?: () => string;
  /**
   * Loopback session token (§15.1).
   *
   * Sent on every request, read as well as write: the Control API authenticates
   * uniformly, and a read that skipped it would be a hole rather than a
   * convenience. `null` when the page was not served by a daemon, in which case
   * every call fails with `missing_session` — legibly, rather than silently
   * returning nothing.
   */
  readonly sessionToken: string | null;
  readonly firebaseToken?: (() => Promise<string | null>) | undefined;
  readonly csrfToken: string | null;
}

/** Headers every request carries, whatever it does. */
async function authHeaders(options: ApiOptions): Promise<Record<string, string>> {
  const firebaseToken = await options.firebaseToken?.() ?? null;
  return {
    Accept: 'application/json',
    ...(firebaseToken !== null
      ? { Authorization: `Bearer ${firebaseToken}` }
      : options.sessionToken === null ? {} : { 'x-autonomous-team-session': options.sessionToken }),
  };
}

/** The team every request means RIGHT NOW — the switcher's choice, not the served default. */
function liveTeamId(options: ApiOptions): string {
  return options.currentTeamId?.() ?? options.teamId;
}

/**
 * Every request names the team it means (§multi-team). The server treats an
 * absent teamId as «the default team», so old paths keep working; paths that
 * already carry their own teamId are left alone.
 */
function withTeam(options: ApiOptions, path: string): string {
  const teamId = liveTeamId(options);
  if (teamId.length === 0 || path.includes('teamId=')) return path;
  return `${path}${path.includes('?') ? '&' : '?'}teamId=${encodeURIComponent(teamId)}`;
}

function forTeam(path: string, teamId: string | undefined): string {
  if (teamId === undefined || teamId.length === 0) return path;
  return `${path}${path.includes('?') ? '&' : '?'}teamId=${encodeURIComponent(teamId)}`;
}

async function request<T>(options: ApiOptions, source: string, path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${options.baseUrl}${withTeam(options, path)}`, {
      ...init,
      headers: { ...await authHeaders(options), ...(init?.headers ?? {}) },
      // Same-origin only: the token is a loopback credential and must never be
      // attached to a cross-origin request.
      credentials: 'same-origin',
    });
  } catch (cause) {
    // A network failure is retryable; anything the server actually answered is
    // classified from its status below.
    throw new ApiError(source, 0, `The control plane did not answer: ${String(cause)}`, true);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    /*
     * The API wraps failures as {error:{message,…}}. Surface the sentence a
     * person can act on; the raw envelope on screen read as «че это».
     */
    let message = body.length === 0 ? `${String(response.status)} ${response.statusText}` : body.slice(0, 400);
    let code: string | null = null;
    try {
      const parsed = JSON.parse(body) as { error?: { code?: unknown; message?: unknown } };
      if (typeof parsed.error?.message === 'string' && parsed.error.message.length > 0) {
        message = parsed.error.message;
      }
      if (typeof parsed.error?.code === 'string' && parsed.error.code.length > 0) code = parsed.error.code;
    } catch {
      // Not JSON: the truncated text stands.
    }
    throw new ApiError(source, response.status, message, response.status >= 500 || response.status === 429, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch (cause) {
    throw new ApiError(source, response.status, `The response was not valid JSON: ${String(cause)}`, false);
  }
}

function mutation(
  options: ApiOptions,
  context: MutationContext,
  body?: unknown,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'POST',
): RequestInit {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      // §20.1: identity and idempotency are part of the contract, not options.
      'Idempotency-Key': context.idempotencyKey,
      'X-Actor-Id': context.actorId,
      'X-Actor-Type': 'human',
      // §15.1: a state-changing request needs the CSRF token as well as the
      // session one, so a hostile page cannot ride the browser's credentials.
      ...(options.csrfToken === null ? {} : { 'x-autonomous-team-csrf': options.csrfToken }),
    },
    body: body === undefined ? '{}' : JSON.stringify(body),
  };
}

/**
 * A mutation carrying files.
 *
 * `multipart/form-data`, so a file crosses the wire as a file. The alternative
 * — base64 in a JSON field — inflates every byte by a third and makes the
 * browser hold the whole attachment as a string before it can send any of it.
 *
 * `Content-Type` is deliberately absent: the browser sets it, and only the
 * browser can, because the header has to carry the multipart boundary it
 * generated. Setting it by hand produces a request whose declared boundary does
 * not match the body's, which every parser rejects.
 */
function upload(
  options: ApiOptions,
  context: MutationContext,
  fields: Readonly<Record<string, string>>,
  files: readonly File[],
): RequestInit {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  for (const file of files) form.append('files', file, file.name);

  return {
    method: 'POST',
    headers: {
      'Idempotency-Key': context.idempotencyKey,
      'X-Actor-Id': context.actorId,
      'X-Actor-Type': 'human',
      ...(options.csrfToken === null ? {} : { 'x-autonomous-team-csrf': options.csrfToken }),
    },
    body: form,
  };
}

/** The live control-plane data source. */
export function createApiDataSource(options: ApiOptions): ConsoleDataSource {
  const encoded = encodeURIComponent;
  return {
    kind: 'live',
    label: 'Control API',
    description: `Live projection from ${options.baseUrl === '' ? 'this origin' : options.baseUrl}.`,

    sceneSnapshot: () => request<SceneSnapshot>(options, 'office projection', '/v1/events/stream?snapshot=1&projection=office'),
    subscribeScene: (cursor: string | null, handlers: SceneStreamHandlers): Subscription =>
      subscribeToScene(
        { baseUrl: options.baseUrl, resumeCursor: () => cursor, sessionToken: options.sessionToken },
        cursor,
        handlers,
      ),

    teamStatus: () => request<TeamStatus>(options, 'team status', `/v1/teams/${encoded(liveTeamId(options))}/status`),
    // Team-scoped reads carry the team. The API requires it explicitly rather
    // than inferring "the only active team", because inference is what turns a
    // second team into a silent cross-team read.
    roles: () => request<readonly RoleSummary[]>(options, 'roles', `/v1/roles?teamId=${encoded(liveTeamId(options))}`),
    roleThread: (roleInstanceId) =>
      request<ThreadView>(options, 'logical thread', `/v1/roles/${encoded(roleInstanceId)}/thread`),
    tasks: () => request<readonly TaskListItem[]>(options, 'tasks', `/v1/tasks?teamId=${encoded(liveTeamId(options))}`),
    task: (taskBindingId) => request<TaskSnapshotView>(options, 'task snapshot', `/v1/tasks/${encoded(taskBindingId)}`),
    previousTaskSnapshot: (taskBindingId) =>
      request<TaskSnapshotView | null>(options, 'task snapshot', `/v1/tasks/${encoded(taskBindingId)}?revision=previous`),
    runs: (taskBindingId) => request<readonly RunView[]>(options, 'runs', `/v1/tasks/${encoded(taskBindingId)}/runs`),
    checklists: (taskBindingId) => request<readonly TaskChecklistView[]>(options, 'checklists', `/v1/tasks/${encoded(taskBindingId)}/checklists?teamId=${encoded(liveTeamId(options))}`),
    evidence: (taskBindingId) =>
      request<readonly EvidenceView[]>(options, 'evidence', `/v1/tasks/${encoded(taskBindingId)}/evidence`),
    artifacts: (taskBindingId) =>
      request<readonly ArtifactView[]>(options, 'artifacts', `/v1/tasks/${encoded(taskBindingId)}/artifacts`),
    audit: (filter) => {
      const query = new URLSearchParams();
      query.set('limit', String(filter.limit ?? 100));
      return request<readonly AuditEntry[]>(options, 'audit log', `/v1/audit?${query.toString()}`);
    },
    board: (epicId, cursor) => {
      const query = new URLSearchParams({ limit: '100' });
      if (epicId !== undefined) query.set('epicId', epicId);
      if (cursor !== undefined && cursor !== null) query.set('cursor', cursor);
      return request<BoardView>(options, 'board', `/v1/board?${query.toString()}`);
    },
    epics: async () => (await request<{ epics: readonly import('./types.js').EpicView[] }>(options, 'epics', `/v1/epics?teamId=${encoded(liveTeamId(options))}`)).epics,
    epic: (epicId) => request<import('./types.js').EpicDetailView>(options, 'epic', `/v1/epics/${encoded(epicId)}?teamId=${encoded(liveTeamId(options))}`),
    deployments: (page) =>
      request<ReleasePageView>(options, 'deployments', releaseListApiPath('deployments', page)),
    deployment: (deploymentId) =>
      request<ReleaseOperationView>(options, 'deployment', releaseDetailApiPath('deployments', deploymentId)),
    releases: (page) => request<ReleasePageView>(options, 'releases', releaseListApiPath('releases', page)),
    release: (releaseId) =>
      request<ReleaseOperationView>(options, 'release', releaseDetailApiPath('releases', releaseId)),
    releasePlans: async () => (await request<{ releases: readonly import('./types.js').ReleasePlanView[] }>(options, 'release plans', '/v1/release-plans')).releases,
    releasePlan: (releaseId) => request<import('./types.js').ReleasePlanView>(options, 'release plan', `/v1/release-plans/${encoded(releaseId)}`),
    releaseConfiguration: () => request<import('./types.js').ReleaseConfigurationView>(options, 'release configuration', '/v1/release-configuration'),
    createReleasePlan: (title, context, cards) => request<import('./types.js').ReleasePlanView['release']>(options, 'create release', '/v1/release-plans', mutation(options, context, { title, ...(cards === undefined ? {} : { cards }) })),
    addReleaseTask: (releaseId, card, context) => request<void>(options, 'add task to release', `/v1/release-plans/${encoded(releaseId)}/items`, mutation(options, context, { card })),
    addReleaseTasks: (releaseId, cards, context) => request<void>(options, 'add tasks to release', `/v1/release-plans/${encoded(releaseId)}/items/bulk`, mutation(options, context, { cards })),
    removeReleaseTask: (releaseId, card, context) => request<void>(options, 'remove task from release', `/v1/release-plans/${encoded(releaseId)}/items/${encoded(card)}`, mutation(options, context, undefined, 'DELETE')),
    deployRelease: (releaseId, action, environment, context) => request<void>(options, 'deploy release', `/v1/release-plans/${encoded(releaseId)}/deploy`, mutation(options, context, { action, environment })),
    cancelReleaseRequest: (releaseId, context) => request<void>(options, 'cancel release request', `/v1/release-plans/${encoded(releaseId)}/cancel-request`, mutation(options, context, {})),
    settleReleasePlan: (releaseId, status, context) => request<void>(options, 'settle release', `/v1/release-plans/${encoded(releaseId)}`, mutation(options, context, { status }, 'PATCH')),
    deleteReleasePlan: (releaseId, context) => request<void>(options, 'delete release', `/v1/release-plans/${encoded(releaseId)}`, mutation(options, context, undefined, 'DELETE')),
    skillContents: async () =>
      (await request<{ skills: readonly SkillContentView[] }>(options, 'skill content', '/v1/skills')).skills,
    saveSkill: (ref, name, text, context, enabled) =>
      request<void>(options, 'skill save', `/v1/skills/${encoded(ref)}`, {
        ...mutation(options, context, { name, text, ...(enabled === undefined ? {} : { enabled }) }),
        method: 'PUT',
      }),
    resetSkill: (ref, context) =>
      request<void>(options, 'skill reset', `/v1/skills/${encoded(ref)}`, {
        ...mutation(options, context),
        method: 'DELETE',
      }),
    teamVersions: async () =>
      (
        await request<{ versions: readonly TeamVersionSummary[] }>(
          options,
          'team versions',
          `/v1/teams/${encoded(liveTeamId(options))}/versions`,
        )
      ).versions,
    skills: () => request<readonly SkillPackageView[]>(options, 'skill packages', '/v1/skills'),
    integrations: () => request<readonly IntegrationView[]>(options, 'integrations', '/v1/integrations'),
    gates: () => request<readonly GateView[]>(options, 'policy gates', '/v1/policies/gates'),
    humanReviews: () =>
      request<readonly HumanReviewRequestView[]>(options, 'human reviews', '/v1/human-reviews'),
    approvals: () => request<readonly ApprovalReceiptView[]>(options, 'approval receipts', '/v1/policies/approvals'),
    proposals: async (filter) => {
      const query = new URLSearchParams();
      if (filter?.kind !== undefined) query.set('kind', filter.kind);
      if (filter?.status !== undefined) query.set('status', filter.status);
      if (filter?.sourceTaskBindingId !== undefined) query.set('sourceTaskBindingId', filter.sourceTaskBindingId);
      const suffix = query.size === 0 ? '' : `?${query.toString()}`;
      return (await request<{ proposals: readonly import('@autonomous-team/domain').Proposal[] }>(options, 'proposals', `/v1/proposals${suffix}`)).proposals;
    },
    editProposal: async (proposalId, revision, draft, context) =>
      (await request<{ proposal: import('@autonomous-team/domain').Proposal }>(options, 'edit proposal', `/v1/proposals/${encoded(proposalId)}`, mutation(options, context, { revision, draft }, 'PATCH'))).proposal,
    approveProposal: async (proposalId, revision, note, context) =>
      (await request<{ proposal: import('@autonomous-team/domain').Proposal }>(options, 'approve proposal', `/v1/proposals/${encoded(proposalId)}/approve`, mutation(options, context, { revision, note }))).proposal,
    rejectProposal: async (proposalId, revision, note, context) =>
      (await request<{ proposal: import('@autonomous-team/domain').Proposal }>(options, 'reject proposal', `/v1/proposals/${encoded(proposalId)}/reject`, mutation(options, context, { revision, note }))).proposal,
    templates: () => request<readonly TeamTemplate[]>(options, 'team templates', '/v1/teams/templates'),
    mainOperatorThread: () => request<MainOperatorThread>(options, 'main operator', '/v1/main-operator/thread'),

    operatorStatus: () => request<OperatorStatusView>(options, 'operator', '/v1/operator/status'),
    listTeams: async () => (await request<{ teams: readonly TeamListItem[] }>(options, 'teams', '/v1/teams')).teams,
    bootstrapTeam: async (slug, displayName, context) =>
      // Straight through `mutation`: hand-rolling the headers here set both
      // `Content-Type` and `content-type`, which fetch joins into the invalid
      // «application/json, application/json» — a 415 the operator saw as «nothing
      // happened». One header, the same as every other POST.
      (await request<{ team: TeamListItem }>(options, 'new team', '/v1/teams/bootstrap', mutation(options, context, { slug, displayName }))).team,
    operatorChats: async () =>
      (await request<{ chats: readonly OperatorChatSummary[] }>(options, 'operator chats', '/v1/operator/chats')).chats,
    openRoleChat: async (roleId, context) =>
      (
        await request<{ chat: OperatorChatSummary }>(
          options,
          'agent chat',
          `/v1/operator/role-chats/${encoded(roleId)}`,
          mutation(options, context),
        )
      ).chat,
    forkRoleChat: async (roleId, context) =>
      (
        await request<{ chat: OperatorChatSummary }>(
          options,
          'thread fork',
          `/v1/operator/role-chats/${encoded(roleId)}/fork`,
          mutation(options, context),
        )
      ).chat,
    homeThread: async (chatId) =>
      (
        await request<{ messages: readonly HomeThreadMessage[] }>(
          options,
          'agent thread',
          `/v1/operator/chats/${encoded(chatId)}/home-thread`,
        )
      ).messages,
    createOperatorChat: async (context) =>
      (
        await request<{ chat: OperatorChatSummary }>(
          options,
          'operator chats',
          '/v1/operator/chats',
          mutation(options, context, {}),
        )
      ).chat,
    ensureTaskChat: (taskBindingId, context) =>
      request<OperatorChatThread>(
        options,
        'task chat',
        `/v1/tasks/${encoded(taskBindingId)}/chat?teamId=${encoded(liveTeamId(options))}`,
        mutation(options, context, {}),
      ),
    taskChat: (taskBindingId, beforeSequence) =>
      request<OperatorChatThread>(
        options,
        'task chat',
        `/v1/tasks/${encoded(taskBindingId)}/chat?teamId=${encoded(liveTeamId(options))}${beforeSequence === undefined ? '' : `&before=${encoded(String(beforeSequence))}`}`,
      ),
    sendTaskChatMessage: (taskBindingId, messageId, content, context, files) => {
      const path = `/v1/tasks/${encoded(taskBindingId)}/chat/messages?teamId=${encoded(liveTeamId(options))}`;
      const stable = { ...context, idempotencyKey: messageId };
      const attached = files ?? [];
      if (attached.length === 0) {
        return request<void>(options, 'task chat message', path, mutation(options, stable, { content }));
      }
      const form = new FormData();
      form.append('content', content);
      for (const file of attached) form.append('files', file, file.name);
      return request<void>(options, 'task chat message', path, {
        method: 'POST',
        headers: {
          'Idempotency-Key': messageId,
          'X-Actor-Id': context.actorId,
          'X-Actor-Type': 'human',
          ...(options.csrfToken === null ? {} : { 'x-autonomous-team-csrf': options.csrfToken }),
        },
        body: form,
      });
    },
    retryTaskChatMessage: async (taskBindingId, messageId, context) => {
      await request<unknown>(
        options,
        'task chat message',
        `/v1/tasks/${encoded(taskBindingId)}/chat/messages/${encoded(messageId)}/retry?teamId=${encoded(liveTeamId(options))}`,
        mutation(options, context, {}),
      );
    },
    stopTaskChat: async (taskBindingId, context) => {
      await request<unknown>(
        options,
        'task chat',
        `/v1/tasks/${encoded(taskBindingId)}/chat/stop?teamId=${encoded(liveTeamId(options))}`,
        mutation(options, context, {}),
      );
    },
    deleteOperatorChat: async (chatId, context) => {
      await request<unknown>(
        options,
        'operator chats',
        `/v1/operator/chats/${encoded(chatId)}`,
        mutation(options, context, {}, 'DELETE'),
      );
    },
    operatorChat: (chatId) =>
      request<OperatorChatThread>(options, 'operator chat', `/v1/operator/chats/${encoded(chatId)}`),
    sendOperatorChatMessage: (chatId, content, context, files) => {
      const path = `/v1/operator/chats/${encoded(chatId)}/messages`;
      const attached = files ?? [];
      if (attached.length === 0) {
        // Plain text is a plain JSON mutation; multipart would be a heavier
        // encoding for a sentence.
        return request<void>(options, 'operator message', path, mutation(options, context, { content }));
      }
      const form = new FormData();
      form.append('content', content);
      for (const file of attached) form.append('files', file, file.name);
      return request<void>(options, 'operator message', path, {
        method: 'POST',
        headers: {
          'Idempotency-Key': context.idempotencyKey,
          'X-Actor-Id': context.actorId,
          'X-Actor-Type': 'human',
          ...(options.csrfToken === null ? {} : { 'x-autonomous-team-csrf': options.csrfToken }),
        },
        body: form,
      });
    },
    transcribeOperatorAudio: async (audio, context) => {
      const form = new FormData();
      form.append('audio', audio, audio.name);
      const result = await request<{ text: string }>(
        options,
        'voice transcription',
        '/v1/operator/transcriptions',
        {
          method: 'POST',
          headers: {
            'Idempotency-Key': context.idempotencyKey,
            'X-Actor-Id': context.actorId,
            'X-Actor-Type': 'human',
            ...(options.csrfToken === null ? {} : { 'x-autonomous-team-csrf': options.csrfToken }),
          },
          body: form,
        },
      );
      return result.text;
    },
    setSkillRoles: async (ref, roles, context) => {
      await request<unknown>(
        options,
        'skill audience',
        `/v1/skills/${encoded(ref)}/roles`,
        mutation(options, context, { roles }, 'PUT'),
      );
    },
    resetOperatorChat: async (chatId, context) => {
      await request<unknown>(
        options,
        'operator chat',
        `/v1/operator/chats/${encoded(chatId)}/reset`,
        mutation(options, context, {}),
      );
    },
    stopOperatorChat: async (chatId, context) => {
      await request<unknown>(
        options,
        'operator chat',
        `/v1/operator/chats/${encoded(chatId)}/stop`,
        mutation(options, context, {}),
      );
    },
    credentialValue: async (name) =>
      (await request<{ value: string }>(options, 'credential value', `/v1/credentials/${encoded(name)}/value`)).value,
    mcpAccessToken: async (context) =>
      await request<{ token: string; persistent: boolean }>(
        options,
        'MCP access token',
        '/v1/mcp/access-token',
        mutation(options, context, {}),
      ),
    attachmentBlob: async (digest) => {
      const response = await fetch(`${options.baseUrl}/v1/board/attachments/${encoded(digest)}`, {
        headers: await authHeaders(options),
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new ApiError('attachment', response.status, `attachment ${digest} did not load`, response.status >= 500);
      }
      return response.blob();
    },

    createTeam: async (slug, displayName, context) => {
      const answer = await request<{ team: { id: string } }>(
        options,
        'team creation',
        '/v1/teams',
        mutation(options, context, { slug, displayName }),
      );
      return { id: answer.team.id };
    },
    validateTeam: (document, context) =>
      request<ValidationReport>(options, 'team validation', '/v1/teams/validate', mutation(options, context, { document })),
    credentials: async () => {
      const answer = await request<{ credentials: readonly CredentialSummaryView[] }>(
        options,
        'credentials',
        '/v1/credentials',
      );
      return answer.credentials;
    },
    webhooks: async (teamId) => {
      const answer = await request<{ webhooks: readonly WebhookEndpointView[] }>(
        options,
        'webhooks',
        forTeam('/v1/webhooks', teamId),
      );
      return answer.webhooks;
    },
    webhookBatches: async (endpointId, teamId) => {
      const answer = await request<{ batches: readonly WebhookBatchView[] }>(
        options,
        'webhook batches',
        forTeam(`/v1/webhooks/${encoded(endpointId)}/batches`, teamId),
      );
      return answer.batches;
    },
    createWebhook: (input: WebhookInputView, context) =>
      request<WebhookSecretView>(options, 'webhook creation', '/v1/webhooks', mutation(options, context, input)),
    updateWebhook: async (endpointId, input, context) => {
      const answer = await request<{ endpoint: WebhookEndpointView }>(
        options,
        'webhook update',
        `/v1/webhooks/${encoded(endpointId)}`,
        mutation(options, context, input, 'PATCH'),
      );
      return answer.endpoint;
    },
    rotateWebhookSecret: (endpointId, input, context) =>
      request<WebhookSecretView>(
        options,
        'webhook secret rotation',
        `/v1/webhooks/${encoded(endpointId)}/rotate-secret`,
        mutation(options, context, input),
      ),
    retryWebhookBatch: async (endpointId, batchId, context) => {
      await request<unknown>(
        options,
        'webhook batch retry',
        `/v1/webhooks/${encoded(endpointId)}/retry`,
        mutation(options, context, { batchId }),
      );
    },
    putCredential: (name, input, context) =>
      request<CredentialSummaryView>(
        options,
        'credential',
        `/v1/credentials/${encoded(name)}`,
        mutation(options, context, input, 'PUT'),
      ),
    setCredentialRoles: (name, roles, context) =>
      request<CredentialSummaryView>(
        options,
        'credential access',
        `/v1/credentials/${encoded(name)}/roles`,
        mutation(options, context, { roles }, 'PUT'),
      ),
    removeCredential: async (name, context) => {
      await request<unknown>(
        options,
        'credential',
        `/v1/credentials/${encoded(name)}`,
        mutation(options, context, undefined, 'DELETE'),
      );
    },
    providerConnections: async () => {
      const answer = await request<{ connections: readonly ProviderConnectionView[] }>(
        options,
        'AI provider connections',
        '/v1/provider-connections',
      );
      return answer.connections;
    },
    stageEntryNotifications: async () => {
      const answer = await request<{ notifications: readonly StageEntryNotificationView[] }>(
        options,
        'stage entry notifications',
        '/v1/stage-entry-notifications',
      );
      return answer.notifications;
    },
    managedLogins: async () => {
      const answer = await request<{ logins: readonly ManagedLoginView[] }>(
        options,
        'signed-in accounts',
        '/v1/provider-connections/managed-logins',
      );
      return answer.logins;
    },
    startProviderLogin: (provider, context) =>
      request<ProviderLoginSessionView>(options, 'provider sign-in', '/v1/provider-connections/login-sessions', mutation(options, context, { provider })),
    providerLoginStatus: (id) =>
      request<ProviderLoginSessionView>(options, 'provider sign-in', `/v1/provider-connections/login-sessions/${encoded(id)}`),
    writeProviderLogin: (id, input, context) =>
      request<ProviderLoginSessionView>(options, 'provider sign-in', `/v1/provider-connections/login-sessions/${encoded(id)}/input`, mutation(options, context, { input })),
    cancelProviderLogin: async (id, context) => {
      await request(options, 'provider sign-in', `/v1/provider-connections/login-sessions/${encoded(id)}`, mutation(options, context, undefined, 'DELETE'));
    },
    createProviderConnection: (input, context) =>
      request<ProviderConnectionView>(
        options,
        'AI provider connection',
        '/v1/provider-connections',
        mutation(options, context, input),
      ),
    updateProviderConnection: (id, input, context) =>
      request<ProviderConnectionView>(
        options,
        'AI provider connection',
        `/v1/provider-connections/${encoded(id)}`,
        mutation(options, context, input, 'PUT'),
      ),
    verifyProviderConnection: (id, context) =>
      request<ProviderConnectionView>(
        options,
        'connection check',
        `/v1/provider-connections/${encoded(id)}/verify`,
        mutation(options, context, {}),
      ),
    removeProviderConnection: async (id, context) => {
      await request<unknown>(
        options,
        'AI provider connection',
        `/v1/provider-connections/${encoded(id)}`,
        mutation(options, context, undefined, 'DELETE'),
      );
    },
    harnesses: async () => {
      const answer = await request<{ harnesses: readonly HarnessSummary[] }>(options, 'agent programs', '/v1/harnesses');
      return answer.harnesses;
    },
    teamWorkflow: (teamId) =>
      request<WorkflowView>(options, 'workflow transitions', `/v1/teams/${encoded(teamId)}/workflow`),
    workflowLayout: async (teamId, _topologySignature) => {
      const answer = await request<{ layout: WorkflowLayoutView | null }>(
        options,
        'workflow layout',
        `/v1/teams/${encoded(teamId)}/workflow-layout`,
      );
      return answer.layout;
    },
    saveWorkflowLayout: async (teamId, input, context) => {
      const answer = await request<{ layout: WorkflowLayoutView }>(
        options,
        'workflow layout',
        `/v1/teams/${encoded(teamId)}/workflow-layout`,
        mutation(options, context, input, 'PUT'),
      );
      return answer.layout;
    },
    packages: async () => {
      const answer = await request<{ packages: readonly PackageOption[] }>(options, 'role packages', '/v1/packages');
      return answer.packages;
    },
    environments: async () => {
      const answer = await request<{ environments: readonly ExecutionEnvironmentSummary[] }>(
        options,
        'execution environments',
        '/v1/environments',
      );
      return answer.environments;
    },
    teamVersionDocument: async (teamId, version) => {
      const answer = await request<{ document: string | null }>(
        options,
        'team version',
        `/v1/teams/${encoded(teamId)}/versions/${encoded(String(version))}`,
      );
      return answer.document;
    },
    /**
     * Drafts a version, and keeps the errors when it will not compile.
     *
     * Deliberately not through `request()`. A document that does not compile is
     * answered with 400 *and a body listing every error at once* — which is
     * AC-001's whole point, "returning only the first error would make fixing a
     * document a game of whack-a-mole". The generic helper turns any non-2xx
     * into one `ApiError` whose message is a truncated blob, so the editor
     * showed "the compiler refused this" and nothing an operator could act on.
     */
    draftTeamVersion: async (teamId, document, context, baseVersion) => {
      const payload = { document, ...(baseVersion === undefined ? {} : { baseVersion }) };
      const response = await fetch(`${options.baseUrl}/v1/teams/${encoded(teamId)}/versions`, {
        ...mutation(options, context, payload),
        headers: { ...await authHeaders(options), ...mutation(options, context, payload).headers },
        credentials: 'same-origin',
      });

      const text = await response.text();
      const body = text.length === 0 ? {} : (JSON.parse(text) as {
        drafted?: boolean;
        version?: { version?: number };
        errors?: readonly { code?: string; message?: string; path?: string }[];
        error?: { code?: string; details?: { version?: number } };
      });

      if (body.drafted === true) {
        return { drafted: true, ...(typeof body.version?.version === 'number' ? { version: body.version.version } : {}) };
      }

      // A refusal with no findings is still a refusal, and saying so beats an
      // empty list that reads as "nothing was wrong".
      /*
       * Saving twice is not an error worth alarming somebody about.
       *
       * A version is content-addressed, so re-drafting an unchanged document is
       * refused as a conflict — correct, and a terrible thing to show an
       * operator as "the compiler refused this document". What actually
       * happened is that this is already saved.
       */
      if (body.error?.code === 'team_version_exists') {
        /*
         * The version number travels with the refusal, because what the screen
         * does next depends on *which* version this already is: the active one
         * means "genuinely nothing changed", a newer one means "your edit is
         * saved and waiting — offer activation". Collapsing both into one
         * sentence left somebody who removed an agent staring at "nothing has
         * changed" with no way forward.
         */
        const existing = body.error.details?.version;
        return {
          drafted: false,
          ...(typeof existing === 'number' ? { version: existing } : {}),
          errors: [
            {
              path: '',
              rule: 'already-saved',
              message:
                existing === undefined
                  ? 'This document is already saved as a version; nothing has changed.'
                  : `This exact document is already saved as version ${String(existing)}.`,
            },
          ],
        };
      }

      const errors: readonly ValidationFinding[] = (body.errors ?? []).map((error) => ({
        path: error.path ?? '',
        rule: error.code ?? 'compile',
        message: error.message ?? 'the compiler gave no message',
      }));

      return {
        drafted: false,
        errors:
          errors.length > 0
            ? errors
            : [{ path: '', rule: `http_${String(response.status)}`, message: text.slice(0, 300) }],
      };
    },
    activateTeamVersion: (teamId, version, context) =>
      request<void>(
        options,
        'team activation',
        `/v1/teams/${encoded(teamId)}/versions/${encoded(String(version))}/activate`,
        mutation(options, context),
      ),
    createCard: (input, context) => request<void>(options, 'card create', '/v1/board/cards', mutation(options, context, input)),
    createEpic: async (title, brief, context) => (await request<{ epic: import('./types.js').EpicView }>(options, 'epic create', `/v1/epics?teamId=${encoded(liveTeamId(options))}`, mutation(options, context, { title, brief }))).epic,
    updateEpic: async (epicId, patch, context) => (await request<{ epic: import('./types.js').EpicView }>(options, 'epic update', `/v1/epics/${encoded(epicId)}?teamId=${encoded(liveTeamId(options))}`, { ...mutation(options, context, patch), method: 'PATCH' })).epic,
    deleteEpic: (epicId, context) => request<import('./types.js').DeleteEpicResult>(
      options,
      'epic deletion',
      `/v1/epics/${encoded(epicId)}?teamId=${encoded(liveTeamId(options))}`,
      mutation(options, context, undefined, 'DELETE'),
    ),
    answerEpicQuestion: (epicId, questionId, answer, context) => request<void>(
      options,
      'epic discovery answer',
      `/v1/epics/${encoded(epicId)}/questions/${encoded(questionId)}/answer?teamId=${encoded(liveTeamId(options))}`,
      mutation(options, context, { answer }, 'PUT'),
    ),
    attachEpicTask: (epicId, externalItemId, context) => request<void>(options, 'epic task attach', `/v1/epics/${encoded(epicId)}/tasks?teamId=${encoded(liveTeamId(options))}`, mutation(options, context, { externalItemId })),
    detachEpicTask: (epicId, externalItemId, context) => request<void>(options, 'epic task detach', `/v1/epics/${encoded(epicId)}/tasks/${encoded(externalItemId)}?teamId=${encoded(liveTeamId(options))}`, { ...mutation(options, context), method: 'DELETE' }),
    setTaskDependency: (epicId, taskId, blockedBy, context) => request<void>(options, 'task dependency', `/v1/epics/${encoded(epicId)}/tasks/${encoded(taskId)}/dependencies/${encoded(blockedBy)}?teamId=${encoded(liveTeamId(options))}`, { ...mutation(options, context), method: 'PUT' }),
    removeTaskDependency: (epicId, taskId, blockedBy, context) => request<void>(options, 'task dependency removal', `/v1/epics/${encoded(epicId)}/tasks/${encoded(taskId)}/dependencies/${encoded(blockedBy)}?teamId=${encoded(liveTeamId(options))}`, { ...mutation(options, context), method: 'DELETE' }),
    editCard: (externalItemId, patch, context) =>
      request<void>(
        options,
        'card edit',
        `/v1/board/cards/${encoded(externalItemId)}`,
        mutation(options, context, patch, 'PATCH'),
      ),
    attachToCard: (externalItemId, input, context) =>
      request<void>(
        options,
        'card attachment',
        `/v1/board/cards/${encoded(externalItemId)}/attachments`,
        upload(options, context, { actorId: context.actorId }, [input]),
      ),
    commentOnCard: (externalItemId, body, context, attachments) => {
      const files = attachments ?? [];
      const path = `/v1/board/cards/${encoded(externalItemId)}/comments`;
      // With no files this is an ordinary JSON mutation; multipart would be a
      // heavier encoding for a sentence.
      const init =
        files.length === 0
          ? mutation(options, context, { actorId: context.actorId, body })
          : upload(options, context, { actorId: context.actorId, body }, files);
      return request<void>(options, 'card comment', path, init);
    },
    pauseRole: (roleInstanceId, context) =>
      request<void>(options, 'role pause', `/v1/roles/${encoded(roleInstanceId)}/pause`, mutation(options, context)),
    resumeRole: (roleInstanceId, context) =>
      request<void>(options, 'role resume', `/v1/roles/${encoded(roleInstanceId)}/resume`, mutation(options, context)),
    pauseTeam: (context) =>
      request<void>(options, 'team pause', `/v1/teams/${encoded(liveTeamId(options))}/pause`, mutation(options, context)),
    resumeTeam: (context) =>
      request<void>(options, 'team resume', `/v1/teams/${encoded(liveTeamId(options))}/resume`, mutation(options, context)),
    quorums: async () => (await request<{ quorums: readonly never[] }>(options, 'quorums', '/v1/quorums')).quorums,
    quorumVersions: async (quorumId) =>
      (await request<{ versions: readonly never[] }>(options, 'quorum versions', `/v1/quorums/${encoded(quorumId)}/versions`)).versions,
    quorumRuns: async () => (await request<{ runs: readonly never[] }>(options, 'quorum runs', '/v1/quorums/runs')).runs,
    createQuorum: (slug, displayName, context) =>
      request<void>(options, 'quorum create', '/v1/quorums', mutation(options, context, { slug, displayName })),
    draftQuorumVersion: async (quorumId, definition, note, context) => {
      const reply = await request<{ version: { version: number } }>(
        options,
        'quorum draft',
        `/v1/quorums/${encoded(quorumId)}/versions`,
        mutation(options, context, { definition, note }),
      );
      return reply.version.version;
    },
    activateQuorumVersion: (quorumId, version, context) =>
      request<void>(options, 'quorum activate', `/v1/quorums/${encoded(quorumId)}/versions/${encoded(String(version))}/activate`, mutation(options, context)),
    usage: async (range) => {
      const window = [
        range?.from !== undefined && range.from.length > 0 ? `from=${encodeURIComponent(range.from)}` : null,
        range?.to !== undefined && range.to.length > 0 ? `to=${encodeURIComponent(range.to)}` : null,
      ]
        .filter((part): part is string => part !== null)
        .join('&');
      const reply = await request<{ usage: readonly never[] }>(
        options,
        'usage',
        `/v1/usage${window.length > 0 ? `?${window}` : ''}`,
      );
      return reply.usage;
    },
    usageExport: async (format, range) => {
      const parts = [`format=${format}`];
      if (range?.from !== undefined && range.from.length > 0) parts.push(`from=${encodeURIComponent(range.from)}`);
      if (range?.to !== undefined && range.to.length > 0) parts.push(`to=${encodeURIComponent(range.to)}`);
      const path = withTeam(options, `/v1/usage?${parts.join('&')}`);
      const response = await fetch(`${options.baseUrl}${path}`, { headers: await authHeaders(options) });
      if (!response.ok) throw new Error(`usage export answered ${String(response.status)}`);
      return response.text();
    },
    taskUsage: (taskBindingId) => request<never>(options, 'task usage', `/v1/tasks/${encoded(taskBindingId)}/usage`),
    stopRole: (roleInstanceId, reason, context) =>
      request<void>(options, 'role stop', `/v1/roles/${encoded(roleInstanceId)}/stop`, mutation(options, context, { reason })),
    stopTeam: (reason, context) =>
      request<void>(options, 'team stop', `/v1/teams/${encoded(liveTeamId(options))}/stop`, mutation(options, context, { reason })),
    sendMainOperatorMessage: (body, context) =>
      request<void>(options, 'main operator', '/v1/main-operator/messages', mutation(options, context, { body })),
    recordHumanDecision: (taskBindingId, decisionId, answer, context) =>
      request<void>(
        options,
        'human decision',
        `/v1/tasks/${encoded(taskBindingId)}/human-decisions`,
        mutation(options, context, { decisionId, answer }),
      ),
    retryStage: (stageRunId, context) =>
      request<void>(options, 'stage retry', `/v1/stage-runs/${encoded(stageRunId)}/retry`, mutation(options, context)),
    cancelRun: (runId, context) =>
      request<void>(options, 'run cancel', `/v1/runs/${encoded(runId)}/cancel`, mutation(options, context)),
  };
}
